import { createFileRoute } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Suspense } from 'react';
import { trpc } from '@/lib/trpc';
import { Header } from './-components/header';
import { Footer } from './-components/footer';
import { Layout } from './-components/layout';
import { PageSeo } from './-components/page-seo';
import { slugs } from './index.meta';
import { Hero } from './-components/home/hero';
import { Solutions } from './-components/home/solutions';
import { Marquee } from './-components/home/marquee';
import { Partners } from './-components/partners';
import { RecentCampaigns } from './-components/home/recent-campaigns';
import { MoreAbout } from './-components/home/more-about';
import { AppDownload } from './-components/home/app-download';

export const Route = createFileRoute('/new/')({
	loader: ({ context }) => {
		// Prefetch the global stats (also carries the collaborating-NGO logos) and the recent campaigns.
		// `prefetchQuery`, not `ensureQueryData`: these are fire-and-forget warm-ups for the
		// `useSuspenseQuery` calls below, and `prefetchQuery` never rejects. With `ensureQueryData` the
		// returned promise is left floating, so when a visitor navigates away or reloads before these
		// slow queries settle, the SSR teardown cancels them, the floating promise rejects with
		// `CancelledError` and — with nothing attached to it — takes the whole server process down.
		context.queryClient.prefetchQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());
		context.queryClient.prefetchQuery(trpc.portal.getRecentCampaigns.queryOptions({ locale: context.locale }));
	},
	component: Component,
});

/**
 * Real partner-merchant logos from getWorldcooGlobalStats → partnerMerchants: every merchant with a
 * logo and a live campaign, ordered by amount raised (biggest first), uncapped upstream.
 */
function PartnersSection() {
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());
	return <Partners merchants={stats.partnerMerchants} />;
}

/** The active campaigns from our system (getRecentCampaigns), localized to the current locale. */
function RecentCampaignsSection() {
	const { locale } = Route.useRouteContext();
	const { data } = useSuspenseQuery(trpc.portal.getRecentCampaigns.queryOptions({ locale }));
	// Real partner-NGO count for the section copy (already prefetched in the loader, no waterfall).
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());
	return <RecentCampaigns campaigns={data.campaigns} ngoCount={stats.socialOrganizations} />;
}

function Component() {
	const { t } = useLingui();

	return (
		<Layout>
			<Header />
			<PageSeo
				slugs={slugs}
				title={t`Worldcoo - Shopping that builds a better world`}
				description={t`We connect companies, consumers and NGOs through transparent fundraising technology to finance social projects all over the world.`}
			/>
			<Hero />
			<Solutions />
			<Marquee />
			{/* Each section stands in for itself while its data streams in, rather than for a bare 600px
			    box: those boxes were 270px and 450px short of the real thing, so everything below them —
			    the whole rest of the page — dropped by three quarters of a screen the moment the two
			    queries landed. A section that draws its own pending state cannot be the wrong size. */}
			<Suspense fallback={<Partners />}>
				<PartnersSection />
			</Suspense>
			<Suspense fallback={<RecentCampaigns />}>
				<RecentCampaignsSection />
			</Suspense>
			<MoreAbout />
			<AppDownload />
			<Footer />
		</Layout>
	);
}
